import React from "react";
import { Check } from "lucide-react";
import { cn } from "@/components/utils/utils";

interface StepperProps {
    steps: Array<{
        label: string;
        description?: string;
    }>;
    currentStep: number;
    skippedSteps?: number[]; // Array of step numbers that are skipped (1-indexed)
    completedSteps?: number[]; // Array of step numbers that are completed (1-indexed)
    className?: string;
}

export const Stepper = ({ steps, currentStep, skippedSteps = [], completedSteps, className }: StepperProps) => {
    return (
        <div className={cn("w-full flex justify-center", className)}>
            <div className="w-full max-w-2xl flex justify-center items-center">
                {steps.map((step, index) => {
                    const stepNumber = index + 1;
                    const isSkipped = skippedSteps.includes(stepNumber);
                    const isCompleted = completedSteps 
                        ? completedSteps.includes(stepNumber) 
                        : (!isSkipped && stepNumber < currentStep);
                    const isCurrent = !isSkipped && stepNumber === currentStep && !completedSteps;
                    const isUpcoming = !isSkipped && stepNumber > currentStep && !completedSteps;

                    return (
                      <React.Fragment key={index}>
                        <div className="flex items-center">
                          {/* Step Indicator */}
                          <div className="flex flex-col items-center">
                            <div
                              className={cn(
                                "w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-colors relative",
                                isSkipped && "bg-gray-100 text-gray-400",
                                isCompleted && !isSkipped && "bg-green-600 text-white",
                                isCurrent && !isSkipped &&
                                  "bg-black text-white ring-4 ring-gray-200",
                                isUpcoming && !isSkipped && "bg-gray-200 text-gray-500",
                              )}
                            >
                              {isCompleted && !isSkipped ? (
                                <Check className="h-5 w-5" />
                              ) : (
                                stepNumber
                              )}
                            </div>
                            <div className="mt-2 text-center">
                              <p
                                className={cn(
                                  "text-sm font-medium transition-colors",
                                  isSkipped && "text-gray-400",
                                  isCurrent && !isSkipped && "text-gray-900",
                                  !isCurrent && !isSkipped && "text-gray-500",
                                )}
                              >
                                {step.label}
                              </p>
                              {step.description && (
                                <p className="text-xs text-gray-400 mt-0.5">
                                  {step.description}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                        {/* Connector Line */}
                        {index < steps.length - 1 && (
                          <div
                            className={cn(
                              "flex-1 mx-4 transition-colors",
                              isSkipped || skippedSteps.includes(stepNumber + 1)
                                ? "h-0.5 bg-gray-200"
                                : stepNumber < currentStep
                                ? "h-0.5 bg-green-600"
                                : "h-0.5 bg-gray-200",
                            )}
                          />
                        )}
                      </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
};
